/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, Fragment, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Flex, Button } from "@duxton-web/ui"
import { replace } from "lodash"
import { ItemAvailabilityModal } from "components/modals/ItemAvailabilityModal"
import { ConfirmModal } from "components/common/ConfirmModal"
import { BottomDrawer } from "components/common"
import notification from "components/common/notification"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { MarkStatusesText, availableStatusesValue } from "utils/item_helper"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory, EventEntryPoint } from "constants/tracking"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import useInventoryContext from "hooks/useInventoryContext"
import {
  resolveV3SelectionForAPI,
  computeV3SelectionSummary,
} from "utils/bulk_selection_helpers"
import {
  flattenInfiniteHierarchies,
  useMenuV3HierarchiesInfinite,
  useDeleteItemV2Mutation,
  useUpdateItemsAvailableV1Mutation,
} from "queries"
import { mapHierarchyEntitiesToDepartments } from "utils/menuV3Mapper"

export interface MultiItemSelectionDrawerV3Props {
  visible: boolean
  onClose(e: any): void
}

export const MultiItemSelectionDrawerV3: FC<MultiItemSelectionDrawerV3Props> = ({
  visible,
  onClose,
}: MultiItemSelectionDrawerV3Props) => {
  const [showStatusModal, toggleStatusModal] = useState(false)
  const [showDeleteModal, toggleDeleteModal] = useState(false)
  const dispatch = useDispatch()
  const { isEditAllowed, isStatusChangeAllowed } = useMenuEditorStatus()
  const [
    deleteItemV2,
    { isLoading: isDeletingItems },
  ] = useDeleteItemV2Mutation()
  const [updateItemsAvailableV1] = useUpdateItemsAvailableV1Mutation()
  const { disableAvailability } = useInventoryContext()

  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    overview: { sellingTime },
  } = useSelector((state: AppState) => state.menu)

  const bulkSelectionsV3 = useSelector(
    (state: AppState) => state.bulkSelectionsV3,
  )

  // Read departments from RTK Query cache — no new network request, data is already loaded
  const { currentData: deptData } = useMenuV3HierarchiesInfinite(
    { merchantID, merchantGroupID },
    { skip: !merchantID },
  )
  const departments = useMemo(
    () =>
      mapHierarchyEntitiesToDepartments(
        flattenInfiniteHierarchies(deptData) ?? [],
      ),
    [deptData],
  )

  const {
    departmentIDs,
    subDepartmentIDs,
    itemIDs,
    excludedSubDepartmentIDs,
    excludedItemIDs,
  } = resolveV3SelectionForAPI(bulkSelectionsV3)

  const { itemCount } = computeV3SelectionSummary(bulkSelectionsV3, departments)
  const isEmpty = itemCount === 0

  const markStatusesTextPlural = MarkStatusesText(true)
  const markStatusesTextSingular = MarkStatusesText(false)

  const textMap = useTranslationsMap({
    changeAvailability: "menu.buttons.changeAvailability",
    cancelButton: "menu.buttons.cancel",
    editSellingTimeButton: "menu.sellingTime.button.editSellingTime",
    deleteTitle: "menu.modals.deleteItems.title",
    deleteContent: "menu.modals.deleteItems.content",
    deleteButton: "menu.buttons.delete",
    deleteItemSuccess: "menu.notifications.deleteItem.successMessage",
    deleteItemsSuccess: "menu.notifications.deleteItems.successMessage",
  })

  const selectionLabel = useTranslation(
    itemCount === 1
      ? "menu.overview.itemSelected"
      : "menu.overview.itemsSelected",
    { "<x>": `${itemCount}` },
  )

  const handleChangeAvailability = async (availableStatus: number) => {
    const totalSelected =
      departmentIDs.length + subDepartmentIDs.length + itemIDs.length

    const labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      ENTRY_POINT: EventEntryPoint.menuOverviewBottomNav,
      DEPARTMENT_ID: departmentIDs,
      SUB_DEPARTMENT_ID: subDepartmentIDs,
      ITEM_ID: itemIDs,
      EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
      EXCLUDED_ITEM_ID: excludedItemIDs,
      NUM_ITEM_SELECTED: itemCount,
      IS_PARTIAL_MENU: true,
      VALUE: availableStatusesValue[availableStatus],
    }

    const { error } = await updateItemsAvailableV1({
      merchantID,
      merchantGroupID,
      availableStatus,
      itemIDs,
      departmentIDs,
      subDepartmentIDs,
      excludedSubDepartmentIDs,
      excludedItemIDs,
    })

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.submitItemAvailability,
      labels: {
        ...labels,
        STATUS: error ? "FAILURE" : "SUCCESS",
        ...(error && getResponseErrorEventLabels(error)),
      },
    })

    if (!error) {
      const successMessage =
        totalSelected === 1
          ? markStatusesTextSingular[availableStatus]
          : replace(
              markStatusesTextPlural[availableStatus],
              "<x>",
              `${totalSelected}`,
            )
      notification.success({ message: successMessage })
    }
  }

  const handleClose = (e: any) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickExitItemAction,
      labels: {
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
        PAGELAYOUT: "BOTTOMNAV",
      },
    })
    onClose(e)
  }

  const handleClickSellingTime = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        showMultiSelectSellingTime: true,
      }),
    )

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.setMultiSelectSellingTime,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : {
              MERCHANT_GROUP_ID: merchantGroupID,
              MENU_GROUP_ID: menuGroupID,
            }),
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
        PAGELAYOUT: "BOTTOMNAV",
        SELLING_TIME_FILTER: sellingTime,
      },
    })
  }

  const handleDelete = async () => {
    const labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      ENTRY_POINT: EventEntryPoint.menuOverviewBottomNav,
      DEPARTMENT_ID: departmentIDs,
      SUB_DEPARTMENT_ID: subDepartmentIDs,
      ITEM_ID: itemIDs,
      EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
      EXCLUDED_ITEM_ID: excludedItemIDs,
      NUM_ITEM_SELECTED: itemCount,
      IS_PARTIAL_MENU: true,
    }

    const { error } = await deleteItemV2({
      merchantID,
      merchantGroupID,
      itemIDs,
      departmentIDs,
      subDepartmentIDs,
      excludedSubDepartmentIDs,
      excludedItemIDs,
    })

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.submitDeleteItem,
      labels: {
        ...labels,
        STATUS: error ? "FAILURE" : "SUCCESS",
        ...(error && getResponseErrorEventLabels(error)),
      },
    })

    if (!error) {
      const message =
        itemCount === 1
          ? textMap.deleteItemSuccess
          : replace(textMap.deleteItemsSuccess, "<x>", `${itemCount}`)
      notification.success({ message })
      toggleDeleteModal(false)
    }
  }

  const handleCancelDelete = () => toggleDeleteModal(false)

  const confirmDelete = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickDeleteItem,
      labels: {
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
        PAGELAYOUT: "BOTTOMNAV",
      },
    })
    toggleDeleteModal(true)
  }

  const handleClickChangeAvailability = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickChangeAvailability,
      labels: {
        DEPARTMENT_ID: departmentIDs,
        SUB_DEPARTMENT_ID: subDepartmentIDs,
        ITEM_ID: itemIDs,
        EXCLUDED_SUB_DEPARTMENT_ID: excludedSubDepartmentIDs,
        EXCLUDED_ITEM_ID: excludedItemIDs,
        NUM_ITEM_SELECTED: itemCount,
        IS_PARTIAL_MENU: true,
        PAGELAYOUT: "BOTTOMNAV",
      },
    })
    toggleStatusModal(true)
  }

  return (
    <Fragment>
      <BottomDrawer
        data-testid="bottom-drawer-v3"
        visible={visible}
        onClose={handleClose}
      >
        <Flex align="center" gap={16}>
          <div style={{ fontWeight: 500, width: 200 }}>{selectionLabel}</div>

          <Button
            data-testid="change-selling-time-button-v3"
            size="small"
            onClick={handleClickSellingTime}
            disabled={!isEditAllowed || isEmpty}
          >
            {textMap.editSellingTimeButton}
          </Button>

          {!disableAvailability && (
            <Button
              data-testid="change-availability-button-v3"
              size="small"
              onClick={handleClickChangeAvailability}
              disabled={!isStatusChangeAllowed || isEmpty}
            >
              {textMap.changeAvailability}
            </Button>
          )}

          <Button
            data-testid="delete-button-v3"
            danger
            size="small"
            onClick={confirmDelete}
            disabled={!isEditAllowed || isEmpty}
          >
            {textMap.deleteButton}
          </Button>
        </Flex>
      </BottomDrawer>

      <ItemAvailabilityModal
        visible={showStatusModal}
        hasRecurring={false}
        hasNonRecurring
        itemCount={itemCount}
        onClose={() => toggleStatusModal(false)}
        onConfirm={handleChangeAvailability}
      />
      <ConfirmModal
        title={replace(textMap.deleteTitle, "<x>", `${itemCount}`)}
        content={textMap.deleteContent}
        visible={showDeleteModal}
        confirmLoading={isDeletingItems}
        onCancel={handleCancelDelete}
        onConfirm={handleDelete}
      />
    </Fragment>
  )
}
